import { getCachedAPIResponse } from "../apiCaching/apiWrapper";
import { apiConfig } from "../config/apiConfig";

const COUNTRY_CODE_MAP: Record<string, string> = {
    US: "USA", CA: "CAN", PR: "PRI", GB: "GBR", MX: "MEX", IN: "IND"
};

const DEFAULT_REGION_CODES = ["us", "ca", "pr"];

// Single Google Maps Platform API key for all Google calls (Geocoding + Places).
// No hardcoded fallback — must be provisioned via environment config (.env.* ->
// googleMapsApiKey), never committed to source.
const resolveGoogleApiKey = (): string | undefined => process.env.googleMapsApiKey;

interface GoogleAddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

interface GoogleGeoResult {
    formatted_address?: string;
    types?: string[];
    address_components?: GoogleAddressComponent[];
    geometry?: { location?: { lat?: number; lng?: number } };
}

const getComponent = (components: GoogleAddressComponent[] | undefined, type: string, useShort = false): string | undefined => {
    const component = components?.find((c) => c.types.includes(type));
    if (!component) return undefined;
    return useShort ? component.short_name : component.long_name;
};

const mapResultType = (types: string[] | undefined): string => {
    if (!types?.length) return "place";
    if (types.some((t) => ["street_address", "street_number", "intersection"].includes(t))) return "street";
    if (types.some((t) => ["premise", "subpremise", "point_of_interest", "establishment"].includes(t))) return "place";
    if (types.includes("postal_code")) return "postalCodePoint";
    if (types.some((t) => ["locality", "postal_town", "sublocality_level_1"].includes(t))) return "city";
    if (types.includes("administrative_area_level_1")) return "state";
    if (types.includes("country")) return "country";
    return "place";
};

const toHereAddress = (result: GoogleGeoResult): any => {
    const components = result.address_components;
    const postalCode = getComponent(components, "postal_code");
    const postalCodeSuffix = getComponent(components, "postal_code_suffix");
    const countryShort = getComponent(components, "country", true);
    return {
        label: result.formatted_address || "",
        houseNumber: getComponent(components, "street_number"),
        street: getComponent(components, "route"),
        district: getComponent(components, "sublocality_level_1") ?? getComponent(components, "neighborhood"),
        city: getComponent(components, "locality") ?? getComponent(components, "postal_town"),
        county: getComponent(components, "administrative_area_level_2"),
        state: getComponent(components, "administrative_area_level_1"),
        stateCode: getComponent(components, "administrative_area_level_1", true),
        postalCode: postalCodeSuffix ? `${postalCode}-${postalCodeSuffix}` : postalCode,
        countryCode: countryShort ? (COUNTRY_CODE_MAP[countryShort] ?? countryShort) : undefined,
        countryName: getComponent(components, "country"),
    };
};

const toHereItem = (result: GoogleGeoResult): any => ({
    title: result.formatted_address || "",
    resultType: mapResultType(result.types),
    position: {
        lat: result.geometry?.location?.lat,
        lng: result.geometry?.location?.lng,
    },
    address: toHereAddress(result),
});

const componentsCountryFilter = (regionCodes: string[]): string =>
    regionCodes.map((code) => `country:${code}`).join("|");

const googleGeocodeFetch = async (params: string): Promise<any> => {
    const url = `${apiConfig.googleGeocodeApiUrl}?${params}&key=${resolveGoogleApiKey()}`;
    const data = await getCachedAPIResponse(url);
    const items = (data?.results ?? []).map(toHereItem);
    return { items };
};

/**
 * Replaces HERE geocode `q=` / `qq=postalCode=` lookups.
 * Returns HERE-shaped { items: [{ title, resultType, position, address }] }.
 * Used for submit-time fallback lookups (handleMissingData, per-form "Submit and
 * Continue" zip/address resolution) — NOT for the live typeahead (see below).
 */
export const googleGeocode = async (query: string, regionCodes: string[] = DEFAULT_REGION_CODES): Promise<any> => {
    const params = `address=${encodeURIComponent(query)}&components=${encodeURIComponent(componentsCountryFilter(regionCodes))}`;
    return googleGeocodeFetch(params);
};

/**
 * Replaces HERE geocode `qq=postalCode={zip}` zip-centroid lookups.
 */
export const googleZipToPosition = async (zip: string, regionCodes: string[] = DEFAULT_REGION_CODES): Promise<{ lat: number; lng: number } | undefined> => {
    const componentsFilter = `postal_code:${zip}|${componentsCountryFilter(regionCodes)}`;
    const params = `components=${encodeURIComponent(componentsFilter)}`;
    const result = await googleGeocodeFetch(params);
    return result?.items?.[0]?.position;
};

/**
 * Replaces HERE revgeocode `at={lat},{lng}` postal-code lookups.
 */
export const googleReverseGeocode = async (lat: number, lng: number): Promise<any> => {
    const params = `latlng=${lat},${lng}&result_type=postal_code&language=en`;
    return googleGeocodeFetch(params);
};

// ---------------------------------------------------------------------------
// Live typeahead (autocomplete) — Places API (New), matching the session-token
// + resolve-on-selection architecture (see dig-ui-react-lead-flow PR #141128,
// googlePlacesAdapter.ts). Suggestion stubs are returned WITHOUT a Details call
// per prediction, so in-session autocomplete requests are billed for free;
// only the single Details call made when the user picks a suggestion is billed.
// ---------------------------------------------------------------------------

const PLACES_AUTOCOMPLETE_URL = "https://places.googleapis.com/v1/places:autocomplete";
const PLACE_DETAILS_URL = "https://places.googleapis.com/v1/places";
const PLACE_DETAILS_FIELD_MASK = "addressComponents,formattedAddress,location,types";

/** One session token per active typeahead control, per Google's Places Autocomplete billing guidance. */
export const createSessionToken = (): string => {
    const cryptoRef = (globalThis as any).crypto;
    if (cryptoRef?.randomUUID) return cryptoRef.randomUUID();
    const bytes = new Uint8Array(16);
    cryptoRef?.getRandomValues?.(bytes);
    return Array.from(bytes, (b: number) => b.toString(16).padStart(2, "0")).join("");
};

interface PlacePredictionNew {
    placeId?: string;
    text?: { text?: string };
}

/**
 * Places API (New) Autocomplete — returns lightweight suggestion stubs
 * (title/label + placeId + sessionToken, no resolved address) as the user
 * types. Full address is resolved only on selection via googleResolvePlaceDetails.
 */
export const googleAutocompleteSuggestions = async (
    query: string,
    sessionToken: string,
    options?: { biasCenter?: { lat: number; lng: number }; radiusMeters?: number; regionCodes?: string[] },
): Promise<any> => {
    if (!query) return { items: [] };
    const regionCodes = (options?.regionCodes ?? DEFAULT_REGION_CODES)
        .filter((code) => code !== "pr")
        .map((code) => code.toUpperCase());
    const body: Record<string, unknown> = {
        input: query,
        includedRegionCodes: regionCodes.length ? regionCodes : ["US", "CA"],
        sessionToken,
    };
    if (options?.biasCenter) {
        body.locationBias = {
            circle: {
                center: { latitude: options.biasCenter.lat, longitude: options.biasCenter.lng },
                radius: options?.radiusMeters ?? 50000,
            },
        };
    }
    try {
        const response = await fetch(PLACES_AUTOCOMPLETE_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-Goog-Api-Key": resolveGoogleApiKey() ?? "" },
            body: JSON.stringify(body),
        });
        const data = await response.json();
        const suggestions = data?.suggestions ?? [];
        const items = suggestions
            .map((s: { placePrediction?: PlacePredictionNew }) => s.placePrediction)
            .filter((p?: PlacePredictionNew): p is PlacePredictionNew => !!p?.placeId)
            .map((p: PlacePredictionNew) => {
                const label = p.text?.text ?? "";
                return {
                    title: label,
                    resultType: "place",
                    position: { lat: undefined, lng: undefined },
                    address: { label },
                    placeId: p.placeId,
                    sessionToken,
                };
            });
        return { items };
    } catch (error) {
        console.error("[addressService] Places Autocomplete (New) error:", error);
        return { items: [] };
    }
};

/**
 * Places API (New) Details — resolves ONE suggestion (by placeId) into a full
 * HERE-shaped item. Call only when the user selects a suggestion; this is the
 * single billed call per session (the session token is retired by the caller
 * after this resolves).
 */
export const googleResolvePlaceDetails = async (placeId: string, sessionToken?: string): Promise<any> => {
    if (!placeId) return null;
    const params = sessionToken ? `?sessionToken=${encodeURIComponent(sessionToken)}` : "";
    try {
        const response = await fetch(`${PLACE_DETAILS_URL}/${encodeURIComponent(placeId)}${params}`, {
            method: "GET",
            headers: {
                "X-Goog-Api-Key": resolveGoogleApiKey() ?? "",
                "X-Goog-FieldMask": PLACE_DETAILS_FIELD_MASK,
            },
        });
        const data = await response.json();
        const item = toHereItem({
            formatted_address: data.formattedAddress,
            types: data.types,
            address_components: (data.addressComponents ?? []).map((c: any) => ({
                long_name: c.longText,
                short_name: c.shortText,
                types: c.types ?? [],
            })),
            geometry: { location: { lat: data.location?.latitude, lng: data.location?.longitude } },
        });
        return item;
    } catch (error) {
        console.error("[addressService] Place Details (New) error:", error);
        return null;
    }
};
